import { types, getSnapshot } from 'mobx-state-tree'

/**
 *
 * @param {function} config.isPersisted function to determine whether a model is saved - input: self. output: bool
 * @param {function} config.keyTransformer transform store values to what the database expects
 */
const buildPersistedChangesStore = (config = {}) => {
  const defaults = {
    isPersisted: self => {
      if (!self.objectId) return false
      return self.objectId.length > 0
    },
    keyTransformer: (self, key) => self[key]
  }

  const settings = {
    ...defaults,
    ...config
  }
  
  const PersistStore = types
    .model('PersistStore', {
      lastPersistedAt: types.maybeNull(types.Date),
      lastValuesSaved: types.frozen({})
    })
    .views(self => ({
      // Checks if current item has been persisted
      get isPersisted() {
        return settings.isPersisted(self)
      },
      // get an object of keyvalues ready to persist. Pick by an array of keys
      buildDataObject(keys = []) {
        const data = {}
        keys.forEach(key => {
          data[key] = settings.keyTransformer(self, key)
        })
        return data
      },
      // like buildDataObject but only plucks selected keys that have been changed since the last persist. This returns false if no changes or no changes in requested keys/values
      buildDataUpdateObject(keys = []) {
        const data = {}
        keys.forEach(key => {
          if (self.hasKeyChanged(key)) {
            data[key] = settings.keyTransformer(self, key)
          }
        })
        if (!Object.values(data).length) return false
        return data
      },
      // Returns true or false if key is different than what was last saved
      hasKeyChanged(key) {
        const currentStore = getSnapshot(self)
        const lastSavedValues = currentStore.lastValuesSaved

        let currentValue
        let lastSavedValue
        // check if key is a function or a string
        if (key instanceof Function) {
          currentValue = key(currentStore)
          lastSavedValue = key(lastSavedValues)
        } else {
          currentValue = currentStore[key]
          lastSavedValue = lastSavedValues[key]
        }

        if (currentValue === null && lastSavedValue === undefined) {
          return false
        }
        switch (typeof currentValue) {
          case 'object': {
            const serializedCurrentValue = JSON.stringify(currentValue)
            const serializedLastSavedValue = JSON.stringify(lastSavedValue)
            return serializedCurrentValue !== serializedLastSavedValue
          }
          case 'string':
          default:
            return currentValue !== lastSavedValue
        }
      }
    }))
    .actions(self => ({
      // Change lastPersistedAt
      updateTime() {
        self.lastPersistedAt = new Date()
      },
      // changed last saved key/value
      setLastUpdateKey(key, value) {
        const newUpdateObject = { ...getSnapshot(self).lastValuesSaved }
        newUpdateObject[key] = value
        self.lastValuesSaved = newUpdateObject
        self.updateTime()
      },
      // Adds dataObject to last saved
      setLastUpdate(dataObj) {
        let target = dataObj
        if (!dataObj) {
          target = getSnapshot(self)
        }
        Object.keys(target).forEach(key =>
          self.setLastUpdateKey(key, target[key])
        )
      },
      removeChangesForKey(key) {
        const snapshot = getSnapshot(self)
        const newlastValuesSaved = { ...snapshot.lastValuesSaved }
        delete newlastValuesSaved[key]
        self.lastValuesSaved = newlastValuesSaved
      },
      restoreLastSavedValue(keys = []) {
        keys.forEach(key => {
          self.restoreLastSavedValueForKey(key)
        })
      },
      restoreLastSavedValueForKey(key) {
        const currentSavedValues = getSnapshot(self).lastValuesSaved
        self[key] = currentSavedValues[key]
      }
    }))

  return PersistStore
}

export default buildPersistedChangesStore
